import axios from "axios";
import { SessionTimeOut } from "../../Components/Utils/SessionTimeOut";
import ReactDOM from "react-dom/client";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";

let isRefreshing = false;
let failedRequestsQueue = []; // holds { resolve, reject } for pending requests

const processQueue = (error, token = null) => {
  failedRequestsQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedRequestsQueue = [];
};

const getBaseUrl = (module) => {
  if(module.includes("/stations-query")){
      return `${window.env.REACT_APP_BASE_URL}`;
  }
  if (
    module.includes("/stations") ||
    module.includes("/getReservation") ||
    module.includes("/reserve") ||
    module.includes("/updateReservation") ||
    module.includes("/reservations") ||
    module.includes("/charging/history")
  ) {
    return `${window.env.REACT_APP_BASE_CSMS_URL}`;
  }
    if (
    module.includes("/partner-offers") || module.includes("/public/upload")
  ) {
    return `${window.env.REACT_APP_BASE_CMS_URL}`;
  }
  if (module.includes("/device") || module.includes("/devices")) {
    return `${window.env.REACT_APP_DEVICE_API}`;
  } else if (
    module.includes("/login") ||
    module.includes("/password/reset/expiry") ||
    module === "/password/reset" ||
    module.includes("/password/set") ||
    module.includes("/password/forget") ||
    module.includes("/user/email") ||
    module.includes("/enquiry") ||
    module.includes("/rbac")
  ) {
    return `${window.env.REACT_APP_AUTH_API}`;
  } else if (module.includes("/payment")) {
    return `${window.env.REACT_APP_PAYMENT_API}`;
  } else if (module.includes("/chat")) {
    return `${window.env.REACT_APP_CHAT_AI}`;
  } else if (
    module.includes("/servicemap") ||
    module.includes("/servicemaps")
  ) {
    return `${window.env.REACT_APP_BASE_ADMIN_URL}`;
  }
  else if(
    module.includes("/getForecast")
  ){
    return `${window.env.REACT_APP_BASE_DATAML_URL}`;
  }
   else {
    return `${window.env.REACT_APP_BASE_URL}`;
  }
};

const api = axios.create();

api.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem("token");
    const operatorID = localStorage.getItem("operatorId");
    const userName = localStorage.getItem("userName");
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      if (config.data instanceof FormData) {
        config.headers["Content-Type"] = "multipart/form-data";
      } else {
        config.headers["Content-Type"] = "application/json";
      }
      config.validateStatus = function (status) {
        return status >= 200 && status <= 504;
      };
      if (config.url.includes("/registerCustomer")) {
        let taskId = uuidv4().replace(/-/g, "").substring(0, 21);
        config.headers["taskId"] = taskId;
      }
      if (config.url.includes("/stockInRequest") || config.url.includes("/stockOutRequest") || config.url.includes("/notifications-sse")) {
        config.headers["userId"] = userName;
      }
      // if (config.url.includes("/reserve") || config.url.includes("/getReservation") || config.url.includes("/updateReservation") || config.url.includes("/stations/metrics/customer") || config.url.includes("/stations")) {
      //     config.headers["OperatorId"] = operatorID
      // }
      // if (config.url.includes("/charging/history")) {
      //     config.headers["OperatorId"] = operatorID
      // }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  async (response) => {
    // ✅ Handle 401 via validateStatus (since you return 200-504 range)
    if (response.status === 401) {
      const originalRequest = response.config;

      // Prevent retry loop on refresh or login endpoints
      if (
        originalRequest.url.includes("/refreshToken") ||
        originalRequest.url.includes("/login")
      ) {
        localStorage.clear();
        renderSessionTimeout();
        return Promise.reject(new Error("Session expired"));
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest); // retry with new token
          })
          .catch((err) => Promise.reject(err));
      }

      // Start refreshing
      isRefreshing = true;
      originalRequest._retry = true;

      try {
        const newToken = await doRefreshToken();
        processQueue(null, newToken); // retry all queued requests
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest); // retry the original request
      } catch (err) {
        processQueue(err, null);
        localStorage.clear();
        renderSessionTimeout();
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    // Your existing response handling
    if (
      response.config.url.includes("/chat") ||
      response.config.url.includes("/user/password/reset/url") ||
      response.config.url.includes("/user/email")
    ) {
      return { ...response.data, status: response.status };
    }
    if (response.config.url.includes("/login")) {
      return {
        ...response.data,
        headers: response.headers,
        status: response.status,
      };
    }
    return response.data;
  },
  async (error) => {
    if (error.response?.status === 401) {
      localStorage.clear();
      renderSessionTimeout();
    }
    return Promise.reject(error);
  }
);

// Extracted helper: actual token refresh logic
export const doRefreshToken = async () => {
  const refreshToken = localStorage.getItem("refreshToken");
  const oldAccessToken = localStorage.getItem("token");

  const response = await axios.post(
    `${window.env.REACT_APP_BASE_URL}/refreshToken`,
    JSON.stringify({ refreshToken }),
    {
      headers: {
        Authorization: `Bearer ${oldAccessToken}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (response.data?.accessToken) {
    localStorage.setItem("token", response.data.accessToken);
    localStorage.setItem("refreshToken", response.data.refreshToken);
    return response.data.accessToken;
  }

  throw new Error("Failed to refresh token");
};

const renderSessionTimeout = () => {
  const root = ReactDOM.createRoot(document.getElementById("csms"));
  root.render(<SessionTimeOut />);
};

export const determineBaseUrlAndPerformApiCall = async (
  endpoint,
  method,
  data
) => {
  api.defaults.baseURL = getBaseUrl(endpoint);
  return await api.request({ method, url: endpoint, data });
};

export default api;
